/**
 * Baghdad Governorate Service Area Geofence
 *
 * Covers the FULL Baghdad Governorate administrative boundary including:
 * - North: Taji, Tarmiyah
 * - East: Mada'in, Jisr Diyala, Rustamiyah, Nahrawan
 * - South: Mahmudiyah, Yusufiyah, Latifiyah, Rasheed
 * - West: Abu Ghraib, Baghdad Airport, Radwaniyah
 * - Central: All Baghdad city districts (Karkh, Rusafa, Sadr City, Kadhimiya, etc.)
 */

// Baghdad Governorate bounding box for Mapbox maxBounds [SW corner, NE corner]
// Covers the entire governorate with padding
export const BAGHDAD_BOUNDS: [[number, number], [number, number]] = [
  [43.85, 32.95],  // Southwest: lng, lat
  [44.85, 33.65],  // Northeast: lng, lat
];

// Baghdad center coordinates
export const BAGHDAD_CENTER = { lat: 33.3152, lng: 44.3661 };

// Baghdad Governorate administrative boundary polygon (lat, lng pairs)
// Generous boundary covering the entire governorate
export const BAGHDAD_POLYGON: [number, number][] = [
  // North — Tarmiyah / Taji
  [33.58, 44.10],  // NW corner — Tarmiyah west
  [33.60, 44.20],  // N — Tarmiyah
  [33.62, 44.30],  // N — north of Taji
  [33.60, 44.42],  // NE — Taji east
  [33.58, 44.55],  // NE corner — towards Khalis border

  // East — Nahrawan / Mada'in
  [33.52, 44.62],  // E — Nahrawan north
  [33.45, 44.68],  // E — Nahrawan
  [33.38, 44.72],  // E — east of Mada'in
  [33.30, 44.72],  // E — Mada'in / Salman Pak approach
  [33.22, 44.68],  // SE — Jisr Diyala south
  [33.15, 44.62],  // SE corner — Salman Pak

  // South — Mahmudiyah / Yusufiyah / Latifiyah
  [33.08, 44.55],  // S — Mahmudiyah east
  [33.05, 44.45],  // S — Mahmudiyah
  [33.03, 44.35],  // S — Yusufiyah / Latifiyah
  [33.05, 44.25],  // S — Latifiyah west
  [33.08, 44.15],  // SW — Yusufiyah west

  // West — Abu Ghraib / Radwaniyah
  [33.15, 44.05],  // SW corner — Abu Ghraib south
  [33.22, 43.98],  // W — Abu Ghraib west
  [33.30, 43.95],  // W — Radwaniyah
  [33.38, 43.95],  // W — west Baghdad
  [33.45, 43.98],  // NW — towards Tarmiyah
  [33.52, 44.02],  // NW — Tarmiyah south
  [33.58, 44.10],  // Close polygon — back to NW corner
];

// GeoJSON for Mapbox overlay — the "mask" that darkens everything outside Baghdad
export const BAGHDAD_MASK_GEOJSON: GeoJSON.FeatureCollection = {
  type: 'FeatureCollection',
  features: [
    {
      type: 'Feature',
      properties: {},
      geometry: {
        type: 'Polygon',
        // Outer ring = world bounds, Inner ring = Baghdad cutout (reversed winding)
        coordinates: [
          // Outer ring (world) — clockwise
          [
            [-180, -90],
            [180, -90],
            [180, 90],
            [-180, 90],
            [-180, -90],
          ],
          // Inner ring (Baghdad cutout) — counter-clockwise to create hole
          BAGHDAD_POLYGON.map(([lat, lng]) => [lng, lat]).reverse(),
        ],
      },
    },
  ],
};

// Baghdad boundary as GeoJSON line for the glowing border
export const BAGHDAD_BORDER_GEOJSON: GeoJSON.FeatureCollection = {
  type: 'FeatureCollection',
  features: [
    {
      type: 'Feature',
      properties: {},
      geometry: {
        type: 'LineString',
        coordinates: BAGHDAD_POLYGON.map(([lat, lng]) => [lng, lat]),
      },
    },
  ],
};

/**
 * Ray-casting point-in-polygon algorithm.
 * Returns true if (lat, lng) is inside the Baghdad Governorate boundary.
 */
export const isInsideBaghdad = (lat: number, lng: number): boolean => {
  const polygon = BAGHDAD_POLYGON;
  let inside = false;

  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const [yi, xi] = polygon[i]; // lat, lng
    const [yj, xj] = polygon[j];

    const intersect =
      yi > lat !== yj > lat && lng < ((xj - xi) * (lat - yi)) / (yj - yi) + xi;

    if (intersect) inside = !inside;
  }

  return inside;
};
